import { useCallback, useEffect } from 'react';
import { useCookieUtil } from 'tradera/hooks/use-cookie-util';
import useIsLoggedIn from 'tradera/hooks/use-is-loggedin';
import { useAppDispatch, useAppSelector } from 'tradera/state/hooks';
import {
    loadMemberServerStorageData,
    saveMemberServerStorageValue,
} from 'tradera/state/member-storage/actions';
import { setValue } from 'tradera/state/member-storage/reducer';

const useMemberServerStorageInternal = <
    TValue,
    TDefaultValue extends TValue | undefined,
>(
    key: string,
    parse: (value: string) => TValue,
    stringify: (value: TValue) => string,
    defaultValue: TDefaultValue,
) => {
    const dispatch = useAppDispatch();
    const isLoggedIn = useIsLoggedIn();
    const isUninitialized = useAppSelector(
        (state) => state.memberStorage.isUnitialized,
    );
    const isSuccess = useAppSelector((state) => state.memberStorage.isSuccess);
    const currentValue = useAppSelector(
        (state) => state.memberStorage.data[key],
    );
    if (isLoggedIn && isUninitialized) {
        dispatch(loadMemberServerStorageData());
    }

    const storeValue = useCallback(
        async (value: TValue) => {
            if (isLoggedIn) {
                return dispatch(
                    saveMemberServerStorageValue(key, stringify(value)),
                );
            }
        },
        [dispatch, isLoggedIn, key, stringify],
    );

    const value =
        currentValue !== undefined ? parse(currentValue) : defaultValue;

    return [value, storeValue, { isReady: isSuccess }] as const;
};

const useMemberServerStorageWithCookieFallbackInternal = <
    TValue,
    TDefaultValue extends TValue | undefined,
>(
    key: string,
    parse: (value: string) => TValue,
    stringify: (value: TValue) => string,
    defaultValue: TDefaultValue,
    expires?: number | string | Date,
) => {
    const dispatch = useAppDispatch();
    const isLoggedIn = useIsLoggedIn();
    const [_value, _storeValue, { isReady: _isReady }] =
        useMemberServerStorageInternal(
            key,
            parse,
            stringify,
            // Use undefined as default value to make it possible to distinguish
            // between default value and no value.
            undefined,
        );

    const cookieUtil = useCookieUtil();
    const isFunctionalCookieEnabled = cookieUtil.gdpr.isFunctionalEnabled();

    // Synchronise state with cookie value
    useEffect(() => {
        // Don't overwrite existing value
        if (_value !== undefined) return;

        // Don't read cookie if functional cookies are disabled
        if (!isFunctionalCookieEnabled) return;

        // Don't synchronise state if server data is not ready
        if (isLoggedIn && !_isReady) return;

        const cookieValue = cookieUtil.getCookie(key);

        // Nothing to synchronise if we have no cookie value
        if (cookieValue === null || cookieValue === undefined) return;
        const parsedValue = parse(cookieValue);

        if (isLoggedIn) {
            _storeValue(parsedValue);
        } else {
            dispatch(setValue({ key, value: stringify(parsedValue) }));
        }
    }, [
        isLoggedIn,
        isFunctionalCookieEnabled,
        _isReady,
        _value,
        key,
        cookieUtil,
        dispatch,
        _storeValue,
        parse,
        stringify,
    ]);

    const storeValue = useCallback(
        (value: TValue) => {
            if (isLoggedIn) {
                return _storeValue(value);
            }
            const stringValue = stringify(value);
            if (isFunctionalCookieEnabled) {
                cookieUtil.createCookie(key, stringValue, expires);
            }
            dispatch(setValue({ key, value: stringValue }));
        },
        [
            _storeValue,
            cookieUtil,
            isFunctionalCookieEnabled,
            isLoggedIn,
            key,
            stringify,
            dispatch,
            expires,
        ],
    );

    let fallbackValue: TValue | undefined;
    // No use for fallback value if we already have value
    if (!_value) {
        // Can only use cookie if functional cookies are enabled
        if (isFunctionalCookieEnabled) {
            const cookieValue = cookieUtil.getCookie(key);
            if (cookieValue !== null && cookieValue !== undefined) {
                fallbackValue = parse(cookieValue);
            }
        }
    }

    return [
        _value ?? fallbackValue ?? defaultValue,
        storeValue,
        {
            isReady: _isReady || !isLoggedIn,
            isPersistant: isLoggedIn || isFunctionalCookieEnabled,
        },
    ] as const;
};

/**
 * You will get an array with three elements:
 * 1. The value
 * 2. A function to set the value
 * 3. An object with two properties:
 *    - isReady: true if the value is ready to be used
 *    - isPersistant: true if the value is persisted in the server
 * The hook is called with the key and defaultValue.
 *
 * @example
 * const [hasSeenAutoShippingModal, setHasSeenAutoShippingModal, { isReady }] =
 *       useMemberServerStorageBoolean(HAS_SEEN_AUTO_SHIPPING_MODAL, false);
 */
export const useMemberServerStorageBoolean = (
    key: string,
    defaultValue?: boolean,
) =>
    useMemberServerStorageInternal(
        key,
        useCallback((value: string) => value === '1', []),
        useCallback((value: boolean) => (value ? '1' : '0'), []),
        defaultValue,
    );

export const useMemberServerStorageNumber = <
    TDefaultValue extends number | undefined,
>(
    key: string,
    defaultValue: TDefaultValue,
) =>
    useMemberServerStorageInternal(
        key,
        useCallback((value: string) => Number(value), []),
        useCallback((value: number) => value.toString(), []),
        defaultValue,
    );

export const useMemberServerStorageWithCookieFallbackBoolean = (
    key: string,
    defaultValue?: boolean,
    expires?: number | string | Date,
) =>
    useMemberServerStorageWithCookieFallbackInternal(
        key,
        // We also check for value true here since som old cookies might have been set with true/false
        useCallback((value: string) => value === '1' || value === 'true', []),
        useCallback((value: boolean) => (value ? '1' : '0'), []),
        defaultValue,
        expires,
    );

export const useMemberServerStorageWithCookieFallbackString = <
    TValue extends string = string,
>(
    key: string,
    defaultValue?: TValue,
    expires?: number | string | Date,
) =>
    useMemberServerStorageWithCookieFallbackInternal(
        key,
        useCallback((value: string) => value as TValue, []),
        useCallback((value: TValue) => value as string, []),
        defaultValue,
        expires,
    );

export const useMemberServerStorage = <TValue extends unknown | undefined>(
    key: string,
    defaultValue: TValue,
) =>
    useMemberServerStorageInternal(
        key,
        useCallback((value: string) => JSON.parse(value) as TValue, []),
        useCallback((value: TValue) => JSON.stringify(value), []),
        defaultValue,
    );

export const useMemberServerStorageWithCookieFallback = <
    TValue extends unknown | undefined,
>(
    key: string,
    defaultValue: TValue,
    expires?: number | string | Date,
) =>
    useMemberServerStorageWithCookieFallbackInternal(
        key,
        useCallback((value: string) => JSON.parse(value) as TValue, []),
        useCallback((value: TValue) => JSON.stringify(value), []),
        defaultValue,
        expires,
    );
